import {
  Add as AddIcon,
  Email as EmailIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  Box,
  IconButton,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import React, { useEffect } from "react";
import { useState } from "react";

import { UseEmailSetup } from "0.Mine/1.Hooks/Guides/emailSetup";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { EmailSetup } from "0.Mine/3.Interfaces/emailAccount";
import { SafeArgonBox, SafeArgonButton, SafeArgonTypography } from "0.Mine/4.Argon/ArgonComponents";

import DynamicDataCaptureModal, { FieldConfig } from "../Reusable/DataCaptureModal";
// Extend EmailSetup with UI-specific fields.

type UIEmail = EmailSetup & { verified: boolean; primary: boolean };

const fields: FieldConfig[] = [
  {
    key: "username",
    label: "Username",
    type: "text",
    required: true,
  },
  {
    key: "password",
    label: "Password",
    type: "text",
    required: true,
  },
  {
    key: "display_name",
    label: "Nome visualizzato",
    type: "text",
    required: false,
  },
  {
    key: "email_address",
    label: "indirizzo email",
    type: "text",
    required: true,
  },
  {
    key: "incoming_server",
    label: "Posta in entrata (POP3/IMAP)",
    type: "text",
    required: true,
  },
  {
    key: "incoming_port",
    label: "Porta",
    type: "number",
    required: true,
  },
  {
    key: "outgoing_server",
    label: "Posta in uscita (SMTP)",
    type: "text",
    required: true,
  },
  {
    key: "outgoing_port",
    label: "Porta",
    type: "number",
    required: true,
  },
  {
    key: "provider",
    label: "Provider",
    type: "select",
    options: ["Gmail", "Outlook", "Custom"],
    required: true,
  },
];

function EmailConnection() {
  const theme = useTheme();
  const [emails, setEmails] = useState<UIEmail[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [currentEmailId, setCurrentEmailId] = useState<number | null>(null);
  const [showVerificationInfo, setShowVerificationInfo] = useState(false);
  const [email, setEmail] = useState<UIEmail>({
    id: 0,
    businessid: 0,
    username: "",
    password: "",
    email_address: newEmail,
    incoming_server: "",
    incoming_port: 0,
    outgoing_server: "",
    outgoing_port: 0,
    authentication: true,
    display_name: null,
    provider: null,
    use_oauth: false,
    oauth_client_id: null,
    oauth_client_secret: null,
    oauth_refresh_token: null,
    last_synced: null,
    verified: false,
    primary: emails.length === 0,
  });

  const { session } = useBusinessContext();
  const { addEmailSetup, getEmailSetup } = UseEmailSetup();

  useEffect(() => {
    const fetchData = async () => {
      if (session && session.selectedBusiness) {
        const response = await getEmailSetup(session.selectedBusiness.id.toString());
        setEmails(
          response.map((email, index) => ({
            ...email,
            verified: email.authentication ?? false,
            primary: index === 0,
          }))
        );
      }
    };

    fetchData();
  }, [session]);

  const validateEmail = (email: string) =>
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      email.toLowerCase()
    );

  const handleAddEmail = () => {
    if (!newEmail) return setEmailError("L'indirizzo email è obbligatorio");
    if (!validateEmail(newEmail)) return setEmailError("Inserisci un indirizzo email valido");
    if (emails.some((e) => e.email_address.toLowerCase() === newEmail.toLowerCase()))
      return setEmailError("Questo indirizzo email è già stato aggiunto");

    setIsAdding(true);
    setTimeout(async () => {
      const newId = Math.max(...emails.map((e) => e.id), 0) + 1;
      const email: UIEmail = {
        id: newId,
        businessid: 0,
        username: "",
        password: "",
        email_address: newEmail,
        incoming_server: "",
        incoming_port: 0,
        outgoing_server: "",
        outgoing_port: 0,
        authentication: true,
        display_name: null,
        provider: null,
        use_oauth: false,
        oauth_client_id: null,
        oauth_client_secret: null,
        oauth_refresh_token: null,
        last_synced: null,
        verified: false,
        primary: emails.length === 0,
      };
      setEmail(email);
      setEmails([...emails, email]);
      setNewEmail("");
      setIsAdding(false);
      setCurrentEmailId(newId);
      setOpenVerifyDialog(true);
    }, 800);
  };

  const handleVerifyEmail = async (formData) => {
    console.log("Form data received:", formData);
    const updatedEmail = { ...email, ...formData };
    setEmail(updatedEmail);
    await addEmailSetup(session.selectedBusiness.id.toString(), updatedEmail);
  };

  const handleDeleteEmail = (id: number) =>
    !emails.find((e) => e.id === id)?.primary && setEmails(emails.filter((e) => e.id !== id));

  const handleSetPrimary = (id: number) =>
    setEmails(emails.map((e) => ({ ...e, primary: e.id === id })));

  const handleResendVerification = (id: number) => {
    setCurrentEmailId(id);
    setOpenVerifyDialog(true);
  };

  const getPrimaryEmailClass = () =>
    emails.find((e) => e.primary && e.verified) ? "success" : "warning";

  return (
    <>
      <SafeArgonBox mt={4} mb={5}>
        <SafeArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <SafeArgonTypography variant="h4" fontWeight="bold">
              Collega i tuoi indirizzi email
            </SafeArgonTypography>
            <SafeArgonTypography variant="body2" color="text">
              Gestisci e verifica gli indirizzi email associati al tuo account
            </SafeArgonTypography>
          </Box>
          <Chip
            icon={<EmailIcon />}
            label={`${emails.filter((e) => e.verified).length}/${emails.length} verificati`}
            color={getPrimaryEmailClass()}
            variant="outlined"
          />
        </SafeArgonBox>

        <Card sx={{ mb: 4 }}>
          <CardContent>
            <SafeArgonTypography variant="h5" fontWeight="bold" mb={3}>
              Aggiungi un nuovo indirizzo email
            </SafeArgonTypography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  label="Indirizzo email"
                  variant="outlined"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setEmailError("");
                  }}
                  error={!!emailError}
                  helperText={emailError}
                  placeholder="nome@esempio.com"
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: "action.active" }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <SafeArgonButton
                  variant="contained"
                  color="primary"
                  onClick={handleAddEmail}
                  disabled={isAdding}
                  startIcon={
                    isAdding ? <CircularProgress size={20} color="inherit" /> : <AddIcon />
                  }
                  fullWidth
                >
                  {isAdding ? "Aggiunta in corso..." : "Aggiungi email"}
                </SafeArgonButton>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={() => setShowVerificationInfo(!showVerificationInfo)}
                sx={{ mr: 1 }}
              >
                {showVerificationInfo ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
              <SafeArgonTypography
                variant="body2"
                color="text.secondary"
                onClick={() => setShowVerificationInfo(!showVerificationInfo)}
                sx={{ cursor: "pointer" }}
              >
                {showVerificationInfo ? "Nascondi informazioni" : "Come funziona la verifica?"}
              </SafeArgonTypography>
            </Box>
            {showVerificationInfo && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <SafeArgonTypography variant="body2">
                  ATTENZIONE - la verifica dell email e necessaria per salvare il tuo indirizzo. 
                </SafeArgonTypography>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <SafeArgonTypography variant="h5" fontWeight="bold" mb={3}>
              Indirizzi email collegati - attenzione: gli indirizzi email verranno salvati dopo la verifica
            </SafeArgonTypography>
            {emails.length === 0 ? (
              <Alert severity="info">
                Non hai ancora aggiunto alcun indirizzo email. Aggiungi il tuo primo indirizzo
                usando il modulo sopra.
              </Alert>
            ) : (
              <Box>
                {emails.map((e, i) => (
                  <Box key={e.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        bgcolor: e.primary
                          ? alpha(theme.palette.primary.main, 0.05)
                          : "transparent",
                        border: `1px solid ${
                          e.primary ? alpha(theme.palette.primary.main, 0.2) : theme.palette.divider
                        }`,
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ ml: 1, flexGrow: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <SafeArgonTypography
                            variant="body1"
                            fontWeight={e.primary ? "bold" : "regular"}
                          >
                            {e.email_address}
                          </SafeArgonTypography>
                          <Box ml={1} display="flex" alignItems="center">
                            {e.verified && (
                              <Chip
                                icon={<CheckIcon fontSize="small" />}
                                label="Verificato"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                            {e.primary && (
                              <Chip
                                icon={<StarIcon fontSize="small" />}
                                label="Principale"
                                size="small"
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        </Box>
                        <SafeArgonTypography variant="caption" color="text.secondary">
                          {e.primary
                            ? "Utilizzato per notifiche e recupero account"
                            : "Email secondaria"}
                        </SafeArgonTypography>
                      </Box>
                      <Box>
                        {!e.verified && (
                          <SafeArgonButton
                            variant="text"
                            color="info"
                            size="small"
                            onClick={() => handleResendVerification(e.id)}
                            sx={{ mr: 1 }}
                          >
                            Verifica
                          </SafeArgonButton>
                        )}
                        {!e.primary && e.verified && (
                          <IconButton
                            color="primary"
                            onClick={() => handleSetPrimary(e.id)}
                            size="small"
                            sx={{ mr: 1 }}
                            title="Imposta come principale"
                          >
                            <StarBorderIcon fontSize="small" />
                          </IconButton>
                        )}
                        {!e.primary && (
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteEmail(e.id)}
                            size="small"
                            title="Rimuovi"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Paper>
                    {i < emails.length - 1 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
                <Box sx={{ mt: 3 }}>
                  <Alert severity="info">
                    <SafeArgonTypography variant="body2">
                      L indirizzo email principale viene usato per notifiche importanti e recupero
                      account. Non può essere eliminato.
                    </SafeArgonTypography>
                  </Alert>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </SafeArgonBox>

      <DynamicDataCaptureModal
        isOpen={openVerifyDialog}
        onClose={() => setOpenVerifyDialog(false)}
        onSubmit={handleVerifyEmail}
        initialData={email}
        fields={fields}
        modalTitle="Verifica indirizzo email"
      />
    </>
  );
}

export default EmailConnection;
